import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, type Params, useRouteError } from "react-router";
import type { NavigationItem } from "store/layout/types";
import type { Scenario, Task } from "common/types";
import { type EndSessionRequest } from "@azure/msal-browser";
import BlockUi from "components/common/BlockUi";
import PageNotFound from "components/errors/PageNotFound";
import Forbidden from "components/errors/Forbidden";
import config, { loginRequest } from "../config";
import { msalReady } from "../auth/msal";

const LayoutContainer = lazy(() => import("containers/LayoutContainer"));

const ProjectManagement = lazy(() => import("containers/ProjectManagement"));
const TaskManagement = lazy(() => import("containers/TaskManagement"));
const ScenarioStatus = lazy(() => import("containers/ScenarioStatus"));

const Scenarios = lazy(() => import("containers/task/Scenarios"));
const ScenarioGroups = lazy(() => import("containers/task/ScenarioGroups"));
const Tags = lazy(() => import("containers/task/Tags"));
const Probes = lazy(() => import("containers/task/Probes"));
const OTIManagement = lazy(() => import("containers/task/OTI/OTIManagement"));
const ResourceAdequacy = lazy(() => import("containers/task/ResourceAdequacy"));

const CubeManagement = lazy(() => import("containers/reporting/CubeManagement"));
const CubeGroups = lazy(() => import("containers/reporting/CubeGroups"));
const StaticReportsAthena = lazy(() => import("containers/reporting/StaticReportsAthena"));
const StaticReportsSnowflake = lazy(() => import("containers/reporting/StaticReportsSnowflake"));
const PSOResults = lazy(() => import("containers/reporting/PSOResults"));
const PSOLogsDashboard = lazy(() => import("containers/reporting/PSOLogsDashboard"));
const StackReportDashboard = lazy(() => import("containers/reporting/StackReportDashboard"));
const ComparerDashboard = lazy(() => import("containers/reporting/ComparerDashboard"));
const DashboardViewer = lazy(() => import("containers/reporting/DashboardViewer"));
const OneLineDiagram = lazy(() => import("containers/reporting/OneLineDiagram"));
// Heat map is disabled: MapDashboard is built on deck.gl 8 / react-map-gl 5, which are not compatible
// with React 19. The container (containers/reporting/MapDashboard) and everything it uses
// (actions/mapActions, reducers/mapReducer, components/map) are kept in the repo but imported nowhere,
// so they stay out of the bundle and deck.gl / react-map-gl are removed from package.json. To bring the
// map back: restore the packages on versions that support React 19, then uncomment this import, the
// `mapDashboard` route below, the menu item in containers/reporting/RootComponent and the `map` reducer
// in reducers/rootReducer.
// const MapDashboard = lazy(() => import("containers/reporting/MapDashboard"));

const AccountsManager = lazy(() => import("containers/adminManagement/AccountsManager"));
const InstanceManager = lazy(() => import("containers/adminManagement/InstanceManager"));
const EnelytixBilling = lazy(() => import("containers/adminManagement/EnelytixBilling"));
const DashboardManagement = lazy(() => import("containers/adminManagement/DashboardManagement"));
const Artifacts = lazy(() => import("containers/adminManagement/Artifacts"));

const logoutRequest: EndSessionRequest = {
    postLogoutRedirectUri: config.msal.postLogoutRedirectUri,
};

const AuthenticatedLayout = () => {
    const signOut = async () => {
        localStorage.removeItem("token");
        const instance = await msalReady!;
        await instance.logoutRedirect(logoutRequest);
    };

    return (
        <Suspense
            fallback={
                <BlockUi
                    blocking
                    renderChildren={false}
                    className="vh-100 d-flex align-items-center justify-content-center"
                />
            }
        >
            <LayoutContainer signOut={signOut} />
        </Suspense>
    );
};

const AuthenticationFailed = () => {
    const error = useRouteError();

    return (
        <div className="vh-100 d-flex align-items-center justify-content-center text-black">
            An Error Occurred: {error instanceof Error ? error.message : String(error)}
        </div>
    );
};

export type RouteHandle = {
    section?: 'task' | 'reporting' | 'management';
    crumbs?: (params: Readonly<Params<string>>, context: CrumbContext) => NavigationItem[];
};

export type CrumbContext = {
    task: Task | null;
    scenario: Scenario | null;
};

export const router = createBrowserRouter([
    {
        element: <AuthenticatedLayout />,
        loader: async () => {
            const instance = await msalReady;

            if (!instance) {
                return null;
            }

            const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];

            if (!account) {
                await instance.acquireTokenRedirect({
                    ...loginRequest,
                    redirectStartPage: window.location.href,
                });
            }

            return null;
        },
        errorElement: <AuthenticationFailed />,
        hydrateFallbackElement: (
            <BlockUi
                blocking
                renderChildren={false}
                className="vh-100 d-flex align-items-center justify-content-center"
            />
        ),
        children: [
            { index: true, element: <ProjectManagement /> },
            {
                path: "projects",
                element: <ProjectManagement />,
                handle: { crumbs: () => [{ text: "Projects", url: `/projects`, active: true }] } satisfies RouteHandle,
            },
            {
                path: "project/:projectId",
                element: <TaskManagement />,
                handle: {
                    crumbs: ({ projectId }) => [
                        { text: "Projects", url: `/projects` },
                        { text: 'Tasks', url: `/project/${projectId}`, active: true },
                    ],
                } satisfies RouteHandle,
            },
            {
                path: "status/:scenarioId",
                element: <ScenarioStatus />,
                handle: {
                    crumbs: ({ scenarioId }, { scenario }) => scenario && String(scenario.scenarioId) === scenarioId
                        ? [
                            { text: "Projects", url: `/projects` },
                            { text: 'Tasks', url: `/project/${scenario.projectId}` },
                            { text: 'Cases', url: `/task/${scenario.taskId}` },
                            { text: 'Status', url: `/status/${scenarioId}`, active: true },
                        ]
                        : [],
                } satisfies RouteHandle,
            },
            {
                path: "task/:taskId",
                handle: {
                    section: 'task',
                    crumbs: ({ taskId }, { task }) => task && String(task.taskId) === taskId
                        ? [
                            { text: "Projects", url: `/projects` },
                            { text: "Tasks", url: `/project/${task.projectId}` },
                            { text: "Cases", url: `/task/${taskId}`, active: true },
                        ]
                        : [],
                } satisfies RouteHandle,
                children: [
                    { index: true, element: <Navigate to="scenarios" replace /> },
                    { path: "scenarios", element: <Scenarios /> },
                    { path: "groups", element: <ScenarioGroups /> },
                    { path: "tags", element: <Tags /> },
                    { path: "probes", element: <Probes /> },
                    { path: "oti", element: <OTIManagement /> },
                    { path: "resource-adequacy", element: <ResourceAdequacy /> },
                    { path: "*", element: <Navigate to="../scenarios" replace /> },
                ],
            },
            {
                path: "reporting/:taskId",
                handle: {
                    section: 'reporting',
                    crumbs: ({ taskId }, { task }) => task && String(task.taskId) === taskId
                        ? [
                            { text: "Projects", url: `/projects` },
                            { text: "Tasks", url: `/project/${task.projectId}` },
                            { text: "Cases", url: `/task/${taskId}` },
                            { text: "Reporting", url: `/reporting/${taskId}`, active: true },
                        ]
                        : [],
                } satisfies RouteHandle,
                children: [
                    { index: true, element: <Navigate to="cubes" replace /> },
                    { path: "cubes", element: <CubeManagement /> },
                    { path: "cubeGroups", element: <CubeGroups /> },
                    { path: "staticReportsAthena", element: <StaticReportsAthena /> },
                    { path: "staticReportsSnowflake", element: <StaticReportsSnowflake /> },
                    { path: "psoResults", element: <PSOResults /> },
                    { path: "psoLogs", element: <PSOLogsDashboard /> },
                    { path: "stackReportDashboard", element: <StackReportDashboard /> },
                    { path: "comparerDashboard", element: <ComparerDashboard /> },
                    { path: "dashboardsViewer", element: <DashboardViewer /> },
                    { path: "oneLineDiagram", element: <OneLineDiagram /> },
                    // { path: "mapDashboard", element: <MapDashboard /> }, // disabled, see MapDashboard above
                    { path: "*", element: <Navigate to="../cubes" replace /> },
                ],
            },
            {
                path: "management",
                handle: {
                    section: 'management',
                    crumbs: () => [{ text: "Management", url: `/management` }],
                } satisfies RouteHandle,
                children: [
                    { index: true, element: <Navigate to="accounts" replace /> },
                    {
                        path: "accounts",
                        element: <AccountsManager />,
                        handle: { crumbs: () => [{ text: "Accounts", url: `/management/accounts`, active: true }] } satisfies RouteHandle,
                    },
                    {
                        path: "instances",
                        element: <InstanceManager />,
                        handle: { crumbs: () => [{ text: "Instance manager", url: `/management/instances`, active: true }] } satisfies RouteHandle,
                    },
                    {
                        path: "billing",
                        element: <EnelytixBilling />,
                        handle: { crumbs: () => [{ text: "Usage Report", url: `/management/billing`, active: true }] } satisfies RouteHandle,
                    },
                    {
                        path: "dashboardManagement",
                        element: <DashboardManagement />,
                        handle: { crumbs: () => [{ text: "Dashboard Management", url: `/management/dashboardManagement`, active: true }] } satisfies RouteHandle,
                    },
                    {
                        path: "artifacts",
                        element: <Artifacts />,
                        handle: { crumbs: () => [{ text: "Artifacts", url: `/management/artifacts`, active: true }] } satisfies RouteHandle,
                    },
                    { path: "*", element: <Navigate to="../accounts" replace /> },
                ],
            },
            { path: "notAuthorized", element: <PageNotFound /> },
            { path: "forbidden", element: <Forbidden /> },
            { path: "*", element: <PageNotFound /> },
        ],
    },
]);
